import { useState, useEffect } from 'react';
import { MapPin, Users, Calendar, ExternalLink, X, ZoomIn } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GroundOfTheWeek() {
  const [ground, setGround] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API_URL}/api/dashboard/ground-of-the-week`, { withCredentials: true })
      .then((res) => mounted && setGround(res.data))
      .catch(() => mounted && setGround(null));
    return () => {
      mounted = false;
    };
  }, []);

  // ESC to close lightbox + lock body scroll while open
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => e.key === 'Escape' && setLightboxOpen(false);
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen]);

  if (!ground) {
    return (
      <div
        className="w-full h-[320px] bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse"
        data-testid="ground-of-week-loading"
      />
    );
  }

  return (
    <section className="w-full bg-white" data-testid="ground-of-week-section">
      {/* Image banner */}
      <div
        className="relative w-full h-[320px] sm:h-[380px] overflow-hidden bg-[#0A0A0A]"
        style={imageError ? {
          // Fallback gradient so a broken image URL doesn't leave a naked
          // black slab. Deep charcoal → football green tint keeps it on-brand.
          backgroundImage: 'linear-gradient(135deg, #0A0A0A 0%, #1A2E1E 55%, #0A1F14 100%)',
        } : undefined}
      >
        {!imageError && (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            aria-label={`View full image of ${ground.name}`}
            className="group absolute inset-0 w-full h-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#FFCC00] focus:ring-inset"
            data-testid="ground-of-week-expand-btn"
          >
            <img
              src={ground.image_url}
              alt={ground.name}
              referrerPolicy="no-referrer"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              style={{ objectPosition: ground.object_position || 'center 55%' }}
              className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.02] ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              data-testid="ground-of-week-image"
            />
            {/* Zoom hint - visible on hover */}
            <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <span className="inline-flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm">
                <ZoomIn className="w-3.5 h-3.5" />
                View full
              </span>
            </div>
          </button>
        )}
        {/* Dark gradient overlay for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />

        {/* Tag pill in corner */}
        <div className="absolute top-6 left-6 pointer-events-none">
          <span className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm text-[#1A1A1A] text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm shadow-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFCC00] animate-pulse" />
            Ground of the Week
          </span>
        </div>

        {/* Title overlay on image */}
        <div className="absolute bottom-0 left-0 right-0 px-6 sm:px-10 pb-6 sm:pb-8 pointer-events-none">
          <div className="max-w-7xl mx-auto">
            <h2
              className="text-3xl sm:text-5xl font-black uppercase tracking-tighter text-white drop-shadow-lg"
              data-testid="ground-of-week-name"
            >
              {ground.name}
            </h2>
            <div className="flex items-center gap-2 mt-2 text-white/90 text-sm sm:text-base font-medium">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span>{ground.location}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Details strip beneath image */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-5 border-b border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
          {/* Stats */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 flex-shrink-0">
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-[#007AFF]" />
              <span className="text-gray-500 uppercase tracking-wider text-xs font-semibold">Capacity</span>
              <span className="font-bold text-[#1A1A1A]" data-testid="ground-of-week-capacity">
                {ground.capacity}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-[#007AFF]" />
              <span className="text-gray-500 uppercase tracking-wider text-xs font-semibold">Opened</span>
              <span className="font-bold text-[#1A1A1A]">{ground.opened}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 uppercase tracking-wider text-xs font-semibold">Sport</span>
              <span className="font-bold text-[#1A1A1A]">{ground.sport}</span>
            </div>
          </div>

          {/* Fun fact */}
          <p
            className="text-sm text-gray-600 italic flex-1 leading-relaxed border-l-2 border-[#FFCC00] pl-4"
            data-testid="ground-of-week-fact"
          >
            {ground.fact}
          </p>

          {ground.wiki_url && (
            <a
              href={ground.wiki_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#007AFF] hover:text-[#3395FF] transition-colors flex-shrink-0"
              data-testid="ground-of-week-wiki"
            >
              Read more
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && !imageError && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Full image of ${ground.name}`}
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
          data-testid="ground-of-week-lightbox"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(false);
            }}
            aria-label="Close full image"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
            data-testid="ground-of-week-lightbox-close"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          {/* Image + caption. stopPropagation so clicking the image itself doesn't close */}
          <div
            className="relative max-w-7xl max-h-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={ground.image_url}
              alt={ground.name}
              referrerPolicy="no-referrer"
              className="max-w-full max-h-[80vh] object-contain rounded-sm shadow-2xl"
              data-testid="ground-of-week-lightbox-image"
            />
            <div className="text-center text-white/90 max-w-2xl px-2">
              <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight">
                {ground.name}
              </h3>
              <p className="text-xs sm:text-sm text-white/60 mt-1 flex items-center justify-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                {ground.location}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
